import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  Layers,
  Zap,
  BadgeDollarSign,
  Lock,
  Clock,
  Headphones,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

type Reason = {
  title: string;
  description: string;
  icon: LucideIcon;
};

const reasons: Reason[] = [
  { title: "Certified Experts", description: "Data scientists and BI specialists with deep industry experience.", icon: Users },
  { title: "Full-Stack Analytics", description: "From data engineering to visualization — we handle the entire pipeline.", icon: Layers },
  { title: "Real-Time Insights", description: "Live dashboards and alerts so you never miss critical business signals.", icon: Zap },
  { title: "Cost-Effective", description: "Enterprise-grade analytics solutions at competitive, transparent pricing.", icon: BadgeDollarSign },
  { title: "Secure & Compliant", description: "Data handled with encryption, access controls, and compliance best practices.", icon: Lock },
  { title: "Fast Delivery", description: "Agile sprints with quick wins and iterative dashboard rollouts.", icon: Clock },
  { title: "Ongoing Support", description: "Continuous monitoring, model tuning, and dedicated analyst support.", icon: Headphones },
  { title: "Proven ROI", description: "Track record of measurable business impact across 120+ clients.", icon: Trophy },
];

const DaWhyChoose: React.FC = () => (
  <AnimatedSection as="section" className="da-section da-section--alt" aria-labelledby="da-why-title">
    <div className="da-container">
      <SectionHeader
        eyebrow="Why Choose WebTech"
        title="Your Trusted Data Analytics Partner"
        text="We turn complex datasets into clear, actionable intelligence that drives smarter business decisions."
      />

      <div className="da-why-grid">
        {reasons.map((reason, index) => {
          const Icon = reason.icon;
          return (
            <motion.article
              key={reason.title}
              className="da-why-card"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.05 }}
              whileHover={{ y: -6 }}
            >
              <div className="da-why-card__icon">
                <Icon size={22} aria-hidden="true" />
              </div>
              <h3>{reason.title}</h3>
              <p>{reason.description}</p>
            </motion.article>
          );
        })}
      </div>
    </div>
  </AnimatedSection>
);

export default DaWhyChoose;
